// Generic CRUD-collection mutations — the single mutation idiom for v2.
//
// Every admin manager talks to the server through these four functions.
// Each operation is optimistic-by-default:
//   1. Patch the local `ck_cache_coll_<scope>` cache that useResource reads from.
//   2. emitCmsChange(scope) so any mounted subscriber re-reads immediately.
//   3. Fire syncToServer() with the matching HTTP call. The success path emits
//      again (with server-truth data swapped into the cache) and dispatches
//      `ck:sync-ok`; the failure path dispatches `ck:sync-fail` so the offline
//      banner surfaces.
//
// The optimistic state is visible until the server round-trip resolves; on
// success it's replaced with the validated row the server returned, parsed
// through the same Zod schema that the read path uses. On failure the local
// cache is rolled back to its pre-mutation state.
//
// INVARIANT — emit implies a preceding write. Every `emitCmsChange(scope)` in
// this module must be preceded by a `writeCache(scope, ...)` in the same
// block. `useResource` tolerates a bare emit because it answers one with a
// network refetch, but a pure cache reader — `useArticles()` in lib/articles.ts
// — re-reads localStorage and would see pre-mutation rows. Any future
// cache-reading hook inherits that constraint, so this is a rule of the module
// rather than a coincidence of how the paths happen to be written. Enforced by
// src/test/lib/cmsStore-collections-emit.test.ts.
//
// Phase 3 of v2 redesign — see docs/redesign/BRIEF.md §3.2 and §7.

import { z, type ZodType } from 'zod';
import { api, apiGet, apiPost, apiPut, apiDelete, bustApiCache } from './api';
import { emitCmsChange } from './cmsEvents';
import {
  articleRowSchema, serviceRowSchema, offerRowSchema, projectRowSchema,
  testimonialRowSchema, experienceRowSchema, certificationRowSchema,
  speakingEventRowSchema, thoughtLeadershipRowSchema, socialLinkRowSchema,
  mediaRowSchema, servicePageRowSchema,
} from '@/schemas';

// Scopes managed by the CRUD factory (one /api/<scope> per scope, uniform
// shape). Conversations and content_sections are NOT in this set — they have
// bespoke routes and their own helpers.
export type CollectionScope =
  | 'articles' | 'services' | 'offers' | 'projects' | 'testimonials'
  | 'experience' | 'certifications' | 'speaking-events'
  | 'thought-leadership' | 'social-links' | 'media' | 'service-pages';

// Which column is the row's primary key for the URL path `/api/<scope>/<id>`?
// Mirrors the `idColumn` passed into crudRoutes() in app/server/index.ts.
const ID_COLUMN: Record<CollectionScope, string> = {
  articles: 'slug',
  services: 'slug',
  offers: 'slug',
  projects: 'id',
  testimonials: 'id',
  experience: 'id',
  certifications: 'id',
  'speaking-events': 'id',
  'thought-leadership': 'id',
  'social-links': 'id',
  media: 'id',
  'service-pages': 'slug',
};

// Same row schemas useCollection uses on the read path. Re-applied here so the
// optimistic state we drop into the cache is the same shape the components
// already expect (timestamps normalised, arrays coerced, etc.).
const ROW_SCHEMAS: Record<CollectionScope, ZodType<unknown>> = {
  articles: articleRowSchema,
  services: serviceRowSchema,
  offers: offerRowSchema,
  projects: projectRowSchema,
  testimonials: testimonialRowSchema,
  experience: experienceRowSchema,
  certifications: certificationRowSchema,
  'speaking-events': speakingEventRowSchema,
  'thought-leadership': thoughtLeadershipRowSchema,
  'social-links': socialLinkRowSchema,
  media: mediaRowSchema,
  'service-pages': servicePageRowSchema,
};

const CACHE_PREFIX = 'ck_cache_coll_';

function readCache<T>(scope: CollectionScope): T[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${scope}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeCache<T>(scope: CollectionScope, rows: T[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(`${CACHE_PREFIX}${scope}`, JSON.stringify(rows));
  } catch {
    // quota exceeded / private mode — best-effort
  }
}

function idOf<T extends Record<string, unknown>>(scope: CollectionScope, row: T): string | undefined {
  const col = ID_COLUMN[scope];
  const value = row[col];
  return typeof value === 'string' ? value : undefined;
}

// Strip undefined patch entries so we don't accidentally overwrite a server
// field with `undefined`. Server-side updateSchema would 400 on `undefined`
// values anyway, but the local optimistic merge should match server semantics.
function mergePatch<T>(row: T, patch: Partial<T>): T {
  const out = { ...row } as T;
  for (const [k, v] of Object.entries(patch)) {
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

interface OptimisticHandle<T> {
  rollback: () => void;
  apply: (server: T) => void;
}

// Snapshot, mutate, return a handle that rolls back on server error or swaps
// in the validated server row on success. Centralises the optimistic dance so
// each public mutation function below stays small.
function applyOptimistic<T>(
  scope: CollectionScope,
  mutate: (rows: T[]) => { next: T[]; matchId: string | undefined },
): OptimisticHandle<T> {
  const before = readCache<T>(scope);
  const { next, matchId } = mutate(before);
  writeCache(scope, next);
  emitCmsChange(scope);
  return {
    rollback: () => {
      writeCache(scope, before);
      emitCmsChange(scope);
    },
    apply: (server: T) => {
      const schema = ROW_SCHEMAS[scope];
      const parsed = schema.safeParse(server);
      const validated = (parsed.success ? parsed.data : server) as T;
      const rows = readCache<T>(scope);
      const serverId = idOf(scope, validated as Record<string, unknown>);
      const replaceId = serverId ?? matchId;
      const updated = replaceId
        ? rows.some((r) => idOf(scope, r as Record<string, unknown>) === replaceId)
          ? rows.map((r) =>
              idOf(scope, r as Record<string, unknown>) === replaceId ? validated : r,
            )
          : [validated, ...rows]
        : rows;
      writeCache(scope, updated);
      emitCmsChange(scope);
    },
  };
}

function dispatchSyncEvent(name: 'ck:sync-ok' | 'ck:sync-fail', scope: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail: { scope } }));
}

// ─── Public mutation surface ───────────────────────────────────────────────

/**
 * Optimistically prepend a new row to the local cache, POST it to the server,
 * and reconcile with the server-validated row on success. Resolves with the
 * server row; rejects (and rolls back the optimistic state) on failure.
 */
export async function cmsCollectionCreate<T extends Record<string, unknown>>(
  scope: CollectionScope,
  row: Partial<T>,
): Promise<T> {
  const handle = applyOptimistic<T>(scope, (rows) => {
    const provisional = row as T;
    const id = idOf(scope, provisional);
    return { next: [provisional, ...rows], matchId: id };
  });
  try {
    const schema = ROW_SCHEMAS[scope] as ZodType<T>;
    const server = await apiPost<T>(`/api/${scope}`, row, schema);
    handle.apply(server);
    dispatchSyncEvent('ck:sync-ok', scope);
    return server;
  } catch (err) {
    handle.rollback();
    dispatchSyncEvent('ck:sync-fail', scope);
    console.warn(`[cmsStore] create failed (${scope})`, err);
    throw err;
  }
}

/**
 * Optimistically merge a patch into the row identified by `id` (the value of
 * ID_COLUMN[scope]). PUT it to the server. Resolves with the server-validated
 * row; rejects (and rolls back) on failure.
 */
export async function cmsCollectionUpdate<T extends Record<string, unknown>>(
  scope: CollectionScope,
  id: string,
  patch: Partial<T>,
): Promise<T> {
  const handle = applyOptimistic<T>(scope, (rows) => {
    const next = rows.map((r) =>
      idOf(scope, r as Record<string, unknown>) === id ? mergePatch(r, patch) : r,
    );
    return { next, matchId: id };
  });
  try {
    const schema = ROW_SCHEMAS[scope] as ZodType<T>;
    const server = await apiPut<T>(`/api/${scope}/${encodeURIComponent(id)}`, patch, schema);
    handle.apply(server);
    dispatchSyncEvent('ck:sync-ok', scope);
    return server;
  } catch (err) {
    handle.rollback();
    dispatchSyncEvent('ck:sync-fail', scope);
    console.warn(`[cmsStore] update failed (${scope}/${id})`, err);
    throw err;
  }
}

/**
 * Optimistically remove the row identified by `id`. DELETE it on the server.
 * Rolls back the cache on failure so subscribers don't see a phantom delete.
 */
export async function cmsCollectionRemove(
  scope: CollectionScope,
  id: string,
): Promise<void> {
  const handle = applyOptimistic<Record<string, unknown>>(scope, (rows) => {
    const next = rows.filter(
      (r) => idOf(scope, r) !== id,
    );
    return { next, matchId: id };
  });
  try {
    await apiDelete(`/api/${scope}/${encodeURIComponent(id)}`);
    dispatchSyncEvent('ck:sync-ok', scope);
  } catch (err) {
    handle.rollback();
    dispatchSyncEvent('ck:sync-fail', scope);
    console.warn(`[cmsStore] remove failed (${scope}/${id})`, err);
    throw err;
  }
}

/**
 * Optimistically remove many rows in one pass, then POST the id list to the
 * server's /bulk-delete endpoint. Rolls the whole cache back on failure so a
 * partial optimistic delete never sticks. Resolves with the server's
 * `{ deleted, notFound }` split.
 */
export async function cmsCollectionBulkRemove(
  scope: CollectionScope,
  ids: string[],
): Promise<{ deleted: string[]; notFound: string[] }> {
  const idSet = new Set(ids);
  const before = readCache<Record<string, unknown>>(scope);
  const next = before.filter((r) => {
    const id = idOf(scope, r);
    return id === undefined || !idSet.has(id);
  });
  writeCache(scope, next);
  emitCmsChange(scope);
  try {
    const result = await apiPost<{ deleted: string[]; notFound: string[] }>(
      `/api/${scope}/bulk-delete`,
      { ids },
    );
    dispatchSyncEvent('ck:sync-ok', scope);
    return result;
  } catch (err) {
    writeCache(scope, before);
    emitCmsChange(scope);
    dispatchSyncEvent('ck:sync-fail', scope);
    console.warn(`[cmsStore] bulk remove failed (${scope})`, err);
    throw err;
  }
}

export interface CsvImportResult {
  imported: number;
  skipped: number;
  errors: Array<{ line: number; message: string }>;
}

/**
 * Upload a CSV file to a collection's /import-csv endpoint (multipart, field
 * name `file`). Resolves with the per-row import summary.
 *
 * Unlike every other mutation here there is no optimistic state to apply: the
 * endpoint returns only `{ imported, skipped, errors }`, never the created
 * rows. We therefore refetch server truth and write it into the cache before
 * emitting, so this path obeys the same write-then-emit rule as its siblings
 * (see the module header). A bare emit was enough while `useResource` was the
 * only subscriber — it answers an emit with a network refetch — but a pure
 * cache reader like useArticles() would have re-read the pre-import rows.
 *
 * The refetch needs an explicit cache bust: this call goes through `api()`
 * with FormData rather than `apiPost`, and only the apiPost/Put/Delete helpers
 * invalidate the GET cache for the written prefix.
 *
 * If the refetch fails we still emit — subscribers that fetch for themselves
 * recover on their own, which is strictly no worse than the old behaviour.
 */
export async function cmsCollectionImportCsv(
  scope: CollectionScope,
  file: File,
): Promise<CsvImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const result = await api<CsvImportResult>(`/api/${scope}/import-csv`, {
    method: 'POST',
    body: fd,
  });
  try {
    bustApiCache(`/api/${scope}`);
    const rowSchema = ROW_SCHEMAS[scope];
    const rows = await apiGet<unknown>(`/api/${scope}`, { schema: z.array(rowSchema) });
    if (Array.isArray(rows)) writeCache(scope, rows as Record<string, unknown>[]);
  } catch (err) {
    console.warn(`[cmsStore] import-csv cache refresh failed (${scope})`, err);
  }
  emitCmsChange(scope);
  return result;
}

/**
 * Reorder a collection by sending `[{id, sortOrder}, ...]` to the server's
 * /reorder endpoint. The optimistic local state mirrors the new order; on
 * failure we restore the previous order.
 *
 * Only collections whose crudRoutes() options include `sortColumn` accept
 * /reorder. Calling this for a collection without sort support will resolve
 * server-side as 404 → the optimistic cache rolls back.
 */
export async function cmsCollectionReorder(
  scope: CollectionScope,
  orderedIds: string[],
): Promise<void> {
  const before = readCache<Record<string, unknown>>(scope);
  const idIndex = new Map(orderedIds.map((id, i) => [id, i] as const));
  const next = before
    .slice()
    .sort((a, b) => {
      const ai = idIndex.get(idOf(scope, a) ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bi = idIndex.get(idOf(scope, b) ?? '') ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  writeCache(scope, next);
  emitCmsChange(scope);
  const body = orderedIds.map((id, i) => ({ id, sortOrder: i }));
  try {
    const rowSchema = ROW_SCHEMAS[scope];
    const server = await apiPut<unknown>(`/api/${scope}/reorder`, body, z.array(rowSchema));
    if (Array.isArray(server)) {
      writeCache(scope, server as Record<string, unknown>[]);
      emitCmsChange(scope);
    }
    dispatchSyncEvent('ck:sync-ok', scope);
  } catch (err) {
    writeCache(scope, before);
    emitCmsChange(scope);
    dispatchSyncEvent('ck:sync-fail', scope);
    console.warn(`[cmsStore] reorder failed (${scope})`, err);
    throw err;
  }
}

// ─── Config blobs (settings_kv per-key writes) ─────────────────────────────
//
// Not a "collection" but plumbed through the same machinery so admin managers
// that write single JSON blobs (notifications config, site links, etc.) share
// the offline-banner + cache-bust semantics. Exit criterion: same mechanism
// (optimistic + syncToServer + emitCmsChange), even if the signature differs
// because the data shape differs.

// ─── Leads (joined read shape — bespoke route at /api/leads) ───────────────
//
// Leads has a custom route (rate-limited capture endpoint + joined
// contactMessage shape on read) that doesn't fit the CRUD factory mold.
// Mutations still flow through the same syncToServer + emitCmsChange
// pipeline so the admin offline banner + collection bus stay consistent.

export async function cmsLeadUpdate<P extends Record<string, unknown>>(
  id: string,
  patch: P,
): Promise<unknown> {
  try {
    const server = await apiPut(`/api/leads/${encodeURIComponent(id)}`, patch);
    emitCmsChange('leads');
    dispatchSyncEvent('ck:sync-ok', 'leads');
    return server;
  } catch (err) {
    dispatchSyncEvent('ck:sync-fail', 'leads');
    console.warn(`[cmsStore] lead update failed (${id})`, err);
    throw err;
  }
}

export async function cmsLeadDelete(id: string): Promise<void> {
  try {
    await apiDelete(`/api/leads/${encodeURIComponent(id)}`);
    emitCmsChange('leads');
    dispatchSyncEvent('ck:sync-ok', 'leads');
  } catch (err) {
    dispatchSyncEvent('ck:sync-fail', 'leads');
    console.warn(`[cmsStore] lead delete failed (${id})`, err);
    throw err;
  }
}

export async function cmsLeadsBulkDelete(
  ids: string[],
): Promise<{ deleted: string[]; notFound: string[] }> {
  try {
    const result = await apiPost<{ deleted: string[]; notFound: string[] }>(
      '/api/leads/bulk-delete',
      { ids },
    );
    emitCmsChange('leads');
    dispatchSyncEvent('ck:sync-ok', 'leads');
    return result;
  } catch (err) {
    dispatchSyncEvent('ck:sync-fail', 'leads');
    console.warn('[cmsStore] leads bulk delete failed', err);
    throw err;
  }
}

export async function cmsLeadsImportCsv(file: File): Promise<CsvImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const result = await api<CsvImportResult>('/api/leads/import-csv', {
    method: 'POST',
    body: fd,
  });
  emitCmsChange('leads');
  return result;
}

export async function cmsConfigBlobSave<T>(key: string, value: T): Promise<T> {
  try {
    const server = await apiPut<T>(`/api/config/${encodeURIComponent(key)}`, value);
    emitCmsChange('config');
    dispatchSyncEvent('ck:sync-ok', 'config');
    return server;
  } catch (err) {
    dispatchSyncEvent('ck:sync-fail', 'config');
    console.warn(`[cmsStore] config blob save failed (${key})`, err);
    throw err;
  }
}
